import { createSlice, PayloadAction, createEntityAdapter } from "@reduxjs/toolkit";
import { filter, switchMap, map, catchError, bufferTime, mergeMap } from "rxjs/operators";
import { of, concat } from "rxjs";
import { WorkflowApi, Workflow, WorkflowsResponse, WorkflowDetail } from "@/api/workflow.api";
import { RootEpic } from "@/store/types";
import { RabbitMqLog } from "@/features/workflow/services/SignalRWorkflowService";

const AI_PREFIXES = [
  "Pipe Fire Workflow",
  "Pipe HVAC Workflow",
];

// SignalR message types
export interface SignalRWorkflowMessage {
  category: "task" | "workflow";
  event_type: "task_started" | "task_completed" | "task_failed" | "workflow_started" | "workflow_completed" | "workflow_failed";
  id: string;
  task_id?: string;
  task_name?: string;
  workflow_id: string;
  queue?: string;
  timestamp: string;
  monitoring_metadata?: {
    processed_at: string;
    source: string;
    version: string;
  };
  signalr_metadata?: {
    category: string;
    sent_at: string;
    source: string;
  };
  _id?: string;
  _workflow_group?: string;
}

interface WorkflowUpdateBatch {
  workflowId: string;
  status?: "pending" | "running" | "completed" | "failed";
  completed_at?: string | null;
  taskUpdates?: Array<{
    taskId: string;
    status?: "pending" | "running" | "completed" | "failed";
    completed_at?: string | null;
    duration?: number | null;
  }>;
}

// Pure function to transform workflows with system workflows
function transformWorkflows(response: WorkflowsResponse): Workflow[] {
  const { document_id, workflows } = response;

  if (workflows.length === 0) {
    return [];
  }

  // Get the earliest created_at from BE workflows
  const firstWorkflow = workflows[0];
  const firstCreatedAt = firstWorkflow.created_at;

  // Check if all BE workflows are completed
  // Lọc workflow AI
  const aiWorkflows = workflows.filter((w) =>
    AI_PREFIXES.some((prefix) => w.name.startsWith(prefix))
  );

  // Xác định trạng thái
  let completeStatus: "completed" | "failed";
  let completeName = "Hoàn tất upload";

  if (aiWorkflows.some((w) => w.status === "failed")) {
    completeStatus = "failed";
    completeName = "Phân tích AI thất bại";
  } else if (aiWorkflows.some((w) => w.status === "completed")) {
    completeStatus = "completed";
    completeName = "Phân tích AI hoàn tất";
  } else {
    completeStatus = "completed";
    completeName = completeName;
  }

  // Get latest completed_at from all workflows
  const latestCompletedAt = aiWorkflows
    .filter((w) => w.completed_at)
    .reduce<string | null>((latest, w) => {
      if (!w.completed_at) return latest;
      if (!latest) return w.completed_at;

      return new Date(w.completed_at) > new Date(latest)
        ? w.completed_at
        : latest;
    }, null);

  // Create Upload workflow
  const uploadWorkflow: Workflow = {
    id: `${document_id}_upload`,
    name: "Upload File",
    status: "completed",
    created_at: firstCreatedAt,
    started_at: firstCreatedAt,
    completed_at: firstCreatedAt,
    created_by: "system",
    task_count: 1,
    context: {},
  };

  // Create Complete workflow
  const completeWorkflow: Workflow = {
    id: `${document_id}_complete`,
    name: completeName,
    status: completeStatus,
    created_at: firstCreatedAt,
    started_at: firstCreatedAt,
    completed_at: latestCompletedAt,
    created_by: "system",
    task_count: 1,
    context: {},
  };

  // Return in order: Upload, ...BE workflows, Complete
  return [uploadWorkflow, ...workflows, completeWorkflow];
}

// Create entity adapter for normalized workflow storage
const workflowsAdapter = createEntityAdapter<Workflow>({
  sortComparer: false, // We'll maintain order via document index
});

interface DocumentWorkflowIndex {
  ids: string[];
  total: number;
  loading: boolean;
  error: string | null;
}

interface WorkflowDetailState {
  data?: WorkflowDetail;
  loading: boolean;
  error: boolean;
  fetchedAt?: number;
}

interface WorkflowState {
  entities: ReturnType<typeof workflowsAdapter.getInitialState>;
  workflowsByDocumentId: Record<string, DocumentWorkflowIndex>;
  detailsByWorkflowId: Record<string, WorkflowDetailState>;
}

const initialState: WorkflowState = {
  entities: workflowsAdapter.getInitialState(),
  workflowsByDocumentId: {},
  detailsByWorkflowId: {},
};

const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    getWorkflowsRequest: (
      state,
      action: PayloadAction<{
        documentId: string;
        limit?: number;
        offset?: number;
      }>
    ) => {
      const { documentId } = action.payload;

      // Initialize or update document index loading state
      if (!state.workflowsByDocumentId[documentId]) {
        state.workflowsByDocumentId[documentId] = {
          ids: [],
          total: 0,
          loading: true,
          error: null,
        };
      } else {
        state.workflowsByDocumentId[documentId].loading = true;
        state.workflowsByDocumentId[documentId].error = null;
      }
    },
    getWorkflowsSuccess: (
      state,
      action: PayloadAction<WorkflowsResponse>
    ) => {
      const { document_id, total } = action.payload;

      // Transform workflows with system workflows
      const transformedWorkflows = transformWorkflows(action.payload);

      if (transformedWorkflows.length > 0) {
        // Add to entity store (normalized)
        workflowsAdapter.upsertMany(state.entities, transformedWorkflows);

        // Save document index
        state.workflowsByDocumentId[document_id] = {
          ids: transformedWorkflows.map((w) => w.id),
          total: total + 2, // Include Upload and Complete workflows
          loading: false,
          error: null,
        };
      } else {
        // Empty response
        state.workflowsByDocumentId[document_id] = {
          ids: [],
          total: 0,
          loading: false,
          error: null,
        };
      }
    },
    getWorkflowsFailure: (
      state,
      action: PayloadAction<{ documentId: string; error: string }>
    ) => {
      const { documentId, error } = action.payload;

      if (state.workflowsByDocumentId[documentId]) {
        state.workflowsByDocumentId[documentId].loading = false;
        state.workflowsByDocumentId[documentId].error = error;
      }
    },
    clearWorkflows: (state, action: PayloadAction<string>) => {
      const documentId = action.payload;

      // Remove workflows from entity store
      if (state.workflowsByDocumentId[documentId]) {
        const workflowIds = state.workflowsByDocumentId[documentId].ids;
        workflowsAdapter.removeMany(state.entities, workflowIds);
        delete state.workflowsByDocumentId[documentId];
      }
    },
    clearAllWorkflows: (state) => {
      state.entities = workflowsAdapter.getInitialState();
      state.workflowsByDocumentId = {};
    },
    getWorkflowStatusRequest: (
      state,
      action: PayloadAction<{ workflowId: string }>
    ) => {
      const { workflowId } = action.payload;
      
      if (!state.detailsByWorkflowId[workflowId]) {
        state.detailsByWorkflowId[workflowId] = {
          loading: true,
          error: false,
        };
      } else {
        state.detailsByWorkflowId[workflowId].loading = true;
        state.detailsByWorkflowId[workflowId].error = false;
      }
    },
    getWorkflowStatusSuccess: (
      state,
      action: PayloadAction<{ workflowId: string; data: WorkflowDetail }>
    ) => {
      const { workflowId, data } = action.payload;
      
      state.detailsByWorkflowId[workflowId] = {
        data,
        loading: false,
        error: false,
        fetchedAt: Date.now(),
      };
    },
    getWorkflowStatusFailure: (
      state,
      action: PayloadAction<{ workflowId: string }>
    ) => {
      const { workflowId } = action.payload;
      
      if (state.detailsByWorkflowId[workflowId]) {
        state.detailsByWorkflowId[workflowId].loading = false;
        state.detailsByWorkflowId[workflowId].error = true;
      }
    },
    // SignalR actions
    receiveSignalRMessage: (
      _state,
      _action: PayloadAction<RabbitMqLog>
    ) => {
      // This action is handled by epic, no state change needed here
    },
    batchUpdateWorkflows: (
      state,
      action: PayloadAction<WorkflowUpdateBatch[]>
    ) => {
      const updates = action.payload;
      
      // Process each workflow update
      updates.forEach(({ workflowId, status, completed_at, taskUpdates }) => {
        // Update workflow entity if exists
        const workflow = state.entities.entities[workflowId];
        if (workflow && status) {
          workflowsAdapter.updateOne(state.entities, {
            id: workflowId,
            changes: {
              status,
              ...(completed_at !== undefined && { completed_at }),
            },
          });
        }

        // Update workflow detail tasks if exists
        const detailState = state.detailsByWorkflowId[workflowId];
        if (detailState?.data && taskUpdates) {
          taskUpdates.forEach(
            ({ taskId, status: taskStatus, completed_at: taskCompletedAt, duration }) => {
              const taskIndex = detailState.data!.tasks.findIndex(t => t.id === taskId);

              if (taskIndex !== -1) {
                const task = detailState.data!.tasks[taskIndex];

                // Update only if present
                if (taskStatus !== undefined) {
                  task.status = taskStatus;
                }

                if (taskCompletedAt !== undefined) {
                  task.completed_at = taskCompletedAt;
                }

                if (duration !== undefined) {
                  task.duration = duration; 
                }
              }
            }
          );
        }
      });
    },
  },
});

// Epic for getting workflows
export const getWorkflowsEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(workflowActions.getWorkflowsRequest.match),
    switchMap((action) => {
      const { documentId, limit = 100, offset = 0 } = action.payload;

      return concat(
        WorkflowApi.getWorkflowsByDocumentId(documentId, limit, offset).pipe(
          map((response) =>
            workflowActions.getWorkflowsSuccess(response)
          ),
          catchError((error) => {
            console.error("Error fetching workflows:", error);
            return of(
              workflowActions.getWorkflowsFailure({
                documentId,
                error: error?.message || "Failed to fetch workflows",
              })
            );
          })
        ),
      );
    })
  );

// Epic for getting workflow status detail
export const getWorkflowStatusEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(workflowActions.getWorkflowStatusRequest.match),
    switchMap((action) => {
      const { workflowId } = action.payload;

      return WorkflowApi.getWorkflowStatus(workflowId).pipe(
        map((data) =>
          workflowActions.getWorkflowStatusSuccess({ workflowId, data })
        ),
        catchError((error) => {
          console.error("Error fetching workflow status:", error);
          return of(
            workflowActions.getWorkflowStatusFailure({ workflowId })
          );
        })
      );
    })
  );

// Helper function to convert SignalR message to batch update
function processSignalRMessages(
  messages: RabbitMqLog[],
  state: any
): WorkflowUpdateBatch[] {
  const MAX_BATCH_SIZE = 1000;
  const limitedMessages = messages.slice(-MAX_BATCH_SIZE);

  const messagesByWorkflow = new Map<string, RabbitMqLog[]>();

  // Group theo workflow_id (chỉ workflow tồn tại trong store)
  for (const msg of limitedMessages) {
    const workflowId = msg.workflow_id;
    if (!workflowId) continue;

    const workflowExists = state.workflow.entities.entities[workflowId];
    if (!workflowExists) continue;

    if (!messagesByWorkflow.has(workflowId)) {
      messagesByWorkflow.set(workflowId, []);
    }

    messagesByWorkflow.get(workflowId)!.push(msg);
  }

  const batches: WorkflowUpdateBatch[] = [];

  // Convert thành batch update
  for (const [workflowId, msgs] of messagesByWorkflow.entries()) {
    const batch: WorkflowUpdateBatch = { workflowId };

    // -----------------------------
    // Workflow-level updates
    // -----------------------------
    for (const msg of msgs) {
      if (msg.category !== "workflow") continue;

      if (msg.event_type === "workflow_completed") {
        batch.status = "completed";
        batch.completed_at = msg.timestamp ?? null;
      }

      if (msg.event_type === "workflow_failed") {
        batch.status = "failed";
        batch.completed_at = msg.timestamp ?? null;
      }

      if (msg.event_type === "workflow_started") {
        batch.status = "running";
      }
    }

    // -----------------------------
    // Task-level updates
    // -----------------------------
    const taskUpdatesMap = new Map<
      string,
      {
        status?: "pending" | "running" | "completed" | "failed";
        completed_at?: string | null;
        duration?: number | null;
      }
    >();

    for (const msg of msgs) {
      if (msg.category !== "task" || !msg.task_id) continue;

      const taskId = msg.task_id;
      const current = taskUpdatesMap.get(taskId) || {};
      const update = { ...current };

      // Update status nếu có event_type
      switch (msg.event_type) {
        case "task_started":
          update.status = "running";
          break;

        case "task_completed":
          update.status = "completed";
          update.completed_at = msg.timestamp ?? null;
          break;

        case "task_failed":
          update.status = "failed";
          update.completed_at = msg.timestamp ?? null;
          break;
      }

      // Update duration nếu SignalR có gửi
      if (typeof msg.duration === "number") {
        update.duration = msg.duration;
      }

      taskUpdatesMap.set(taskId, update);
    }

    if (taskUpdatesMap.size > 0) {
      batch.taskUpdates = Array.from(taskUpdatesMap.entries()).map(
        ([taskId, update]) => ({
          taskId,
          ...update,
        })
      );
    }

    batches.push(batch);
  }

  return batches;
}

// Epic for processing SignalR messages with batching and debouncing
export const processSignalRMessagesEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(workflowActions.receiveSignalRMessage.match),
    bufferTime(500), // Buffer messages for 500ms
    filter((messages) => messages.length > 0), // Only process if we have messages
    mergeMap((messages) => {
      const signalRMessages = messages.map(action => action.payload);
      const batches = processSignalRMessages(signalRMessages, state$.value);
      
      if (batches.length === 0) {
        return of(); // No updates needed
      }

      console.log(`Processing ${signalRMessages.length} SignalR messages → ${batches.length} batch updates`);
      
      return of(workflowActions.batchUpdateWorkflows(batches));
    })
  );

export const workflowEpics = [
  getWorkflowsEpic, 
  getWorkflowStatusEpic,
  processSignalRMessagesEpic,
];

export const workflowActions = workflowSlice.actions;
export const workflowReducer = workflowSlice.reducer;

// Entity adapter selectors
const adapterSelectors = workflowsAdapter.getSelectors(
  (state: any) => state.workflow.entities
);

// Selectors
export const selectAllWorkflowEntities = adapterSelectors.selectAll;
export const selectWorkflowById = adapterSelectors.selectById;
export const selectWorkflowEntities = adapterSelectors.selectEntities;

// Document-based selectors
export const selectWorkflowsByDocumentId = (documentId: string) => (state: any): Workflow[] => {
  const docIndex = state.workflow.workflowsByDocumentId[documentId];
  if (!docIndex || !docIndex.ids.length) {
    return [];
  }

  return docIndex.ids
    .map((id: string) => state.workflow.entities.entities[id])
    .filter((workflow: Workflow | undefined): workflow is Workflow => workflow !== undefined);
};

export const selectWorkflowLoadingByDocumentId = (documentId: string) => (state: any): boolean => {
  return state.workflow.workflowsByDocumentId[documentId]?.loading ?? false;
};

export const selectWorkflowErrorByDocumentId = (documentId: string) => (state: any): string | null => {
  return state.workflow.workflowsByDocumentId[documentId]?.error ?? null;
};

export const selectWorkflowTotalByDocumentId = (documentId: string) => (state: any): number => {
  return state.workflow.workflowsByDocumentId[documentId]?.total ?? 0;
};

export const selectWorkflowDetailByWorkflowId = (workflowId: string) => (state: any): WorkflowDetailState | undefined => {
  return state.workflow.detailsByWorkflowId[workflowId];
};

// Legacy selectors for backward compatibility (deprecated)
export const selectWorkflows = (state: any) => adapterSelectors.selectAll(state);
export const selectWorkflowLoading = (state: any) => {
  // Returns true if any document is loading
  return Object.values(state.workflow.workflowsByDocumentId).some(
    (doc: any) => doc.loading
  );
};
export const selectWorkflowError = (state: any) => {
  // Returns first error found
  const docWithError = Object.values(state.workflow.workflowsByDocumentId).find(
    (doc: any) => doc.error
  );
  return docWithError ? (docWithError as any).error : null;
};
export const selectWorkflowTotal = (state: any) => {
  // Returns total across all documents
  return Object.values(state.workflow.workflowsByDocumentId).reduce(
    (sum: number, doc: any) => sum + (doc.total || 0),
    0
  );
};
